import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud, StartGate } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

// ゼロ config（sandbox 等）の既定デモ。config 不在時の挙動を従来と不変に保つ
const DEFAULT_EVENTS = [
  { id: 'C', text: '窓が割れる音' },
  { id: 'A', text: '電話が鳴る' },
  { id: 'B', text: '車が発進' },
  { id: 'D', text: '悲鳴が聞こえる' },
];
const DEFAULT_CORRECT_ORDER = ['A', 'D', 'C', 'B'];
const DEFAULT_CONSTRAINTS = [
  { text: '電話の直後に悲鳴' },
  { text: '窓が割れた後に車が発進' },
];

// 非空文字列のみ採用（ホストは raw config を渡す＝Zod default 充填なし。型ゆらぎを自前で吸収する）
const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);

// プレイ条件（timeLimitMs / maxMistakes）の正規化: 正の有限数のみ有効。
// step スキーマの既定 0（=制限なし）と不正値は null に揃える（ゼロ config 回帰安全）
const toPlayLimit = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null;

// 初期表示順: initialOrder があればその並び。載っていない event は元の並びで末尾に補う（欠落 config でも全カードを失わない）
const arrangeInitialEvents = (events, initialOrder) => {
  if (!Array.isArray(initialOrder) || initialOrder.length === 0) return [...events];
  const byId = new Map(events.map((ev) => [ev.id, ev]));
  const arranged = initialOrder.map((id) => byId.get(id)).filter(Boolean);
  const placed = new Set(arranged.map((ev) => ev.id));
  events.forEach((ev) => {
    if (!placed.has(ev.id)) arranged.push(ev);
  });
  return arranged;
};

const Template04Timeline = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_timeline_04', templateId = 4 }) => {
  // T11/T14 と同じ堅牢 config 取り込み: 内容ベースの key で memo し、object identity の揺れに影響されない
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const source = hostConfig ?? {};
    const seenEventIds = new Set();
    const configEvents = Array.isArray(source.events)
      ? source.events.filter((ev) => {
          if (!ev || typeof ev.id !== 'string' || ev.id === '' || typeof ev.text !== 'string') return false;
          if (seenEventIds.has(ev.id)) return false;
          seenEventIds.add(ev.id);
          return true;
        })
      : [];
    const usingDefaultEvents = configEvents.length === 0;
    const events = usingDefaultEvents ? DEFAULT_EVENTS : configEvents;

    // correctOrder 不在時: デモにはデモ正解、config events には「events の記載順 = 正解」で必ず勝てる状態を保つ
    const configOrder = Array.isArray(source.correctOrder)
      ? source.correctOrder.filter((id) => typeof id === 'string' && id !== '')
      : [];
    const correctOrder =
      configOrder.length > 0 ? configOrder : usingDefaultEvents ? DEFAULT_CORRECT_ORDER : events.map((ev) => ev.id);

    // constraints: デモ文言は config events と無関係なため、events を差し替えた場合は既定文言を出さない（パネルごと隠す）
    const constraints = Array.isArray(source.constraints)
      ? source.constraints.filter((c) => c && typeof c.text === 'string' && c.text.trim() !== '')
      : usingDefaultEvents
        ? DEFAULT_CONSTRAINTS
        : [];

    const successMessage = source.successMessage ?? {};
    const failMessage = source.failMessage ?? {};
    return {
      events,
      correctOrder,
      constraints,
      initialOrder: Array.isArray(source.initialOrder) ? source.initialOrder : null,
      // R1 体験文法: StartGate / MinigameHud の見出しと説明（config 優先・既定は常体の一行）
      title: pickText(source.title, '時系列再構成'),
      instruction: pickText(source.instructionText, '出来事を正しい順に並べ、確定せよ'),
      successTitle:
        typeof successMessage.title === 'string' && successMessage.title !== '' ? successMessage.title : '再構成完了！',
      successSubtitle:
        typeof successMessage.subtitle === 'string' && successMessage.subtitle !== ''
          ? successMessage.subtitle
          : '事件の時系列が明らかに',
      // failMessage: 未指定は undefined のまま FailOverlay の既定文言（届かなかった…）に委ねる
      failTitle: pickText(failMessage.title, undefined),
      failSubtitle: pickText(failMessage.subtitle, undefined),
      allowRetry: failMessage.allowRetry !== false,
    };
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const [events, setEvents] = useState(() => arrangeInitialEvents(resolvedConfig.events, resolvedConfig.initialOrder));
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  const [shakeKey, setShakeKey] = useState(0);
  // R1 体験文法: 開始ゲート。step が showTutorial===false を明示した時だけ最初から開けておく
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  const [mistakes, setMistakes] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [failReason, setFailReason] = useState(null); // 'timeout' | 'mistakes'
  const feedbackTimerRef = useRef(null);
  const { submit } = useManualSubmit(registryId, templateId, hostSubmit);

  // プレイ条件: config 優先 → step(hostMeta)。0 / 不正値は「制限なし」（従来挙動のまま）
  const timeLimitMs = toPlayLimit(hostConfig?.timeLimitMs ?? hostMeta?.timeLimitMs);
  const mistakeLimit = toPlayLimit(hostConfig?.maxMistakes ?? hostMeta?.maxMistakes);

  const failGame = (reason) => {
    setFailReason(reason);
    // success との競合は「先に確定した方が勝つ」（playing の時だけ fail に落とす）
    setGameState((prev) => (prev === 'playing' ? 'fail' : prev));
  };

  // 実動タイマー: ゲート表示中は停止。満了で fail（limit 未設定なら remainingMs=null で無効）
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: !gateOpen && gameState === 'playing',
    onExpire: () => failGame('timeout'),
  });

  useEffect(
    () => () => {
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    },
    []
  );

  const moveEvent = (index, direction) => {
    if (gameState !== 'playing') return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= events.length) return;

    const newEvents = [...events];
    [newEvents[index], newEvents[newIndex]] = [newEvents[newIndex], newEvents[index]];
    setEvents(newEvents);
  };

  const checkOrder = () => {
    if (gameState !== 'playing') return;
    const currentOrder = events.map((ev) => ev.id);
    const isCorrect =
      currentOrder.length === resolvedConfig.correctOrder.length &&
      currentOrder.every((id, i) => id === resolvedConfig.correctOrder[i]);
    if (isCorrect) {
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
      setFeedback(null);
      setGameState('success');
      return;
    }
    // 誤順確定 = ミス 1 回。上限到達時は瞬間トーストを出さず FailOverlay（内蔵 shake）へ直行する
    const nextMistakes = mistakes + 1;
    setMistakes(nextMistakes);
    if (mistakeLimit != null && nextMistakes >= mistakeLimit) {
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
      setFeedback(null);
      failGame('mistakes');
      return;
    }
    // R0 で入れた誤順フィードバック（shake + 短文）は不変（上限未設定なら試行回数も従来どおり無制限）
    setShakeKey((key) => key + 1);
    setFeedback('順序が合わない…');
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(() => {
      setFeedback(null);
      feedbackTimerRef.current = null;
    }, 1100);
  };

  // FailOverlay「やり直す」: 内部リセット + retryCount 加算（submit はまだ起きていないので resetSubmission 不要）
  const handleRetry = () => {
    if (feedbackTimerRef.current) {
      window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    setEvents(arrangeInitialEvents(resolvedConfig.events, resolvedConfig.initialOrder));
    setFeedback(null);
    setShakeKey(0);
    setMistakes(0);
    setFailReason(null);
    setGameState('playing');
    setRetryCount((count) => count + 1);
    resetTimer();
  };

  const hasConstraints = resolvedConfig.constraints.length > 0;

  // HUD チップ: showTimer===false / showMistakeCount===false は表示のみ抑止（計時・上限判定は生きる）
  const hudTimer =
    timeLimitMs != null && remainingMs != null && hostConfig?.showTimer !== false
      ? { remainingMs, totalMs: timeLimitMs }
      : null;
  const hudMistakes =
    mistakeLimit != null && hostConfig?.showMistakeCount !== false
      ? { current: mistakes, max: mistakeLimit }
      : null;

  return (
    <div className="t04-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {!backgroundStyle && <div className="t04-bg-overlay absolute inset-0" />}

      {/* R1 体験文法: 独自絵文字ヘッダ＋飾りメニューを撤去し、共通 HUD に置換。? は開始ゲート再表示 */}
      <MinigameHud
        title={resolvedConfig.title}
        icon="Clock"
        timer={hudTimer}
        mistakes={hudMistakes}
        onHelp={() => setGateOpen(true)}
      />

      {hasConstraints && (
        <div className="absolute top-14 left-0 right-0 z-20 p-3">
          <div className="t04-constraint-panel border rounded-xl p-3">
            <p className="t04-constraint-label text-xs font-bold mb-1">制約条件</p>
            {resolvedConfig.constraints.map((constraint, index) => (
              <p key={index} className="t04-constraint-text text-sm">• {constraint.text}</p>
            ))}
          </div>
        </div>
      )}

      {/* 制約パネルが無い config では並べ替え領域を上に広げ、イベント数が多くても収まるようスクロール可能にする */}
      <div
        className={`absolute left-0 right-0 bottom-28 px-4 flex flex-col overflow-y-auto ${hasConstraints ? 'top-40' : 'top-14'}`}
      >
        <div className="my-auto flex flex-col gap-2 py-2">
          {events.map((event, index) => (
            <div key={event.id} className="flex items-center gap-2">
              <div className="flex flex-col gap-1">
                <button
                  className="t04-reorder-btn rounded flex items-center justify-center"
                  onClick={() => moveEvent(index, -1)}
                  disabled={index === 0}
                >
                  <Icon name="ArrowUp" size={16} className={index === 0 ? 't04-reorder-icon--disabled' : 't04-reorder-icon'} />
                </button>
                <button
                  className="t04-reorder-btn rounded flex items-center justify-center"
                  onClick={() => moveEvent(index, 1)}
                  disabled={index === events.length - 1}
                >
                  <Icon name="ArrowDown" size={16} className={index === events.length - 1 ? 't04-reorder-icon--disabled' : 't04-reorder-icon'} />
                </button>
              </div>
              <div className="t04-event-card flex-1 border rounded-xl p-3 flex items-center gap-3">
                <span className="t04-order-badge w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="t04-event-text block">{event.text}</span>
                  {typeof event.timestamp === 'string' && event.timestamp !== '' && (
                    <span className="t04-event-timestamp block text-xs mt-0.5">{event.timestamp}</span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="t04-footer absolute bottom-0 left-0 right-0 z-30 pt-4 pb-6 px-4">
        <button
          className="t04-confirm-btn w-full font-bold py-3 rounded-xl"
          onClick={checkOrder}
        >
          順序を確定
        </button>
      </div>

      {/* 誤順確定の瞬間フィードバック（中央トースト）。成功モーダルではないため JudgementModal には集約しない */}
      {feedback && gameState === 'playing' && (
        <div className="t04-feedback absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 px-6 py-3 rounded-xl font-bold pointer-events-none">
          {feedback}
        </div>
      )}

      {gameState === 'playing' && shakeKey > 0 && (
        <FailureEffect key={shakeKey} type="shake" duration={400} onComplete={() => {}} />
      )}

      {/* R1 体験文法: 開始ゲート（表示中は実動タイマーも停止する） */}
      {gateOpen && (
        <StartGate
          title={resolvedConfig.title}
          instruction={resolvedConfig.instruction}
          icon="Clock"
          onStart={() => setGateOpen(false)}
        />
      )}

      {/* R1 体験文法: fail（時間切れ / ミス上限到達）の明示フィードバック。
          やり直す = 内部リセット / 未整理のまま進む = submit('fail')。文言は config.failMessage 優先 */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle}
          subtitle={resolvedConfig.failSubtitle ?? (failReason === 'timeout' ? '時間切れ' : 'ミスが上限に達した')}
          allowRetry={resolvedConfig.allowRetry}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約）。
          文言は config.successMessage を優先し、無指定なら従来文言 */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successTitle}
        description={resolvedConfig.successSubtitle}
        icon={<Icon name="Clock" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />
    </div>
  );
};

export default Template04Timeline;
